/**
 * @file 统一错误边界
 * 渲染期异常不应该把整页打成白屏：D1 的登录与外壳是后续 14 天所有页面的容器，
 * 这里兜住子树异常，保留「重新加载」这条可操作路径，并打印到控制台便于排障。
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

/** 错误边界属性。 */
interface ErrorBoundaryProps {
  /** 子树。 */
  children: ReactNode;
  /** 自定义降级 UI，不传使用内置卡片。 */
  fallback?: ReactNode;
}

/** 错误边界状态。 */
interface ErrorBoundaryState {
  /** 捕获到的错误；null 表示正常。 */
  error: Error | null;
}

/**
 * @kind     React 错误边界
 * @what     捕获子树渲染异常并展示可恢复的降级页
 * @depends  react
 * @threadsafe 实例级，不共享状态
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  /**
   * @what 派生状态：把抛出的错误存起来
   * @param error 抛出的错误
   * @return ErrorBoundaryState 新状态
   */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  /**
   * @what 记录错误上下文（组件栈）
   * @param error 错误
   * @param info React 错误信息
   * @return void 无返回值
   * @side 写 console.error
   */
  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[lexiloop] 渲染异常已捕获', error, info.componentStack);
  }

  /**
   * @what 渲染子树或降级 UI
   * @return ReactNode
   */
  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return (
      <div className="lx-state" style={{ padding: '72px 32px', margin: '0 auto', maxWidth: 720 }}>
        <div className="lx-state__code">渲染出错</div>
        <h1 className="lx-state__title">这个页面没能显示出来</h1>
        <p className="lx-state__text">
          已拦住白屏。先重新加载一次；如果反复出现，把下面的错误信息和追踪号一起发给技术同学。
        </p>
        <p className="lx-mono" style={{ color: 'var(--lx-ink-3)', margin: '0 0 20px', wordBreak: 'break-all' }}>
          {error.message}
        </p>
        <div className="lx-state__actions">
          <button type="button" className="lx-btn lx-btn--primary" onClick={() => window.location.reload()}>
            重新加载
          </button>
        </div>
      </div>
    );
  }
}
